import { pixelArtNeutral } from "@dicebear/collection";
import { createAvatar } from "@dicebear/core";
import {
  Avatar,
  Button,
  CloseButton,
  Divider,
  Drawer,
  Group,
  MediaQuery,
  Navbar,
  ScrollArea,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCirclePlus } from "@tabler/icons-react";
import dayjs from "dayjs";
import QueryString from "qs";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { Logo } from "@/components/Logo";
import { parsePhone } from "@/utils/helpers";
import useAuthStore from "../../store/useAuthStore";
import DASHBOARD_ROUTES from "../DASHBOARD_ROUTES";
import NavbarSection from "./navbar/NavbarSection";

export default function DashboardNavbar({ opened, toggleOpened, close }) {
  const user = useAuthStore((state) => state.user);
  const permission = useAuthStore((state) => state.permission);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const theme = useMantineTheme();

  const avatar = createAvatar(pixelArtNeutral, {
    seed: user?.id,
  });

  const svg = avatar.toDataUriSync();

  // Fetch requested orders count
  const requestedOrdersParams = QueryString.stringify({
    filters: {
      status: "requested",
      startDate: dayjs().format("YYYY-MM-DD"),
      isFeeOrder: { $null: true },
    },
    pagination: {
      page: 1,
      pageSize: 1,
    },
  });

  const { data: requestedOrdersData } = useSWR(
    permission === "admin" || permission === "freelancer"
      ? ["/orders", requestedOrdersParams]
      : null,
    {
      refreshInterval: 30000, // Refresh every 30 seconds
    },
  );

  const requestedOrdersCount = requestedOrdersData?.meta?.pagination?.total || 0;

  // Fetch fee orders count
  const feeOrdersParams = QueryString.stringify({
    filters: {
      status: "feeRequested",
      startDate: dayjs().format("YYYY-MM-DD"),
      isFeeOrder: true,
    },
    pagination: {
      page: 1,
      pageSize: 1,
    },
  });

  const { data: feeOrdersData } = useSWR(
    permission === "admin" || permission === "freelancer" ? ["/orders", feeOrdersParams] : null,
    {
      refreshInterval: 30000, // Refresh every 30 seconds
    },
  );

  const feeOrdersCount = feeOrdersData?.meta?.pagination?.total || 0;

  function content() {
    return (
      <Navbar width={{ sm: 300, lg: 300 }} hiddenBreakpoint="xs">
        <MediaQuery largerThan="sm" styles={{ display: "none" }}>
          <Divider />
        </MediaQuery>
        <Group spacing="sm" p="md" position="left">
          <Avatar variant="white" size={40} src={svg} radius="md" />
          <div>
            <Text size="sm" weight={500}>
              {user?.company?.name || "배차통 관리자"}
            </Text>
            <Text size="xs" color="dimmed">
              {user?.name} ({parsePhone(user?.phoneNumber)})
            </Text>
          </div>
        </Group>

        <Divider />

        <Navbar.Section grow p="md" component={ScrollArea}>
          <Button
            fullWidth
            size="md"
            mb="sm"
            onClick={() => {
              navigate("/new");
              toggleOpened();
            }}
            leftIcon={<IconCirclePlus size="1rem" />}
          >
            신규 배차 신청
          </Button>

          {DASHBOARD_ROUTES.map((section, idx) => {
            //SECTION PERMISSION CHECK
            return (
              <Fragment key={`${section.title}-${idx}`}>
                {!section.permission.includes(permission) ? null : (
                  <>
                    {section.title && <Divider my="xs" label={section.title} />}
                    {section?.items?.map((route) => {
                      let badge;
                      if (route.url === "/admin/orders") {
                        badge = requestedOrdersCount;
                      } else if (route.url === "/admin/orders/fee") {
                        badge = feeOrdersCount;
                      }
                      return (
                        <NavbarSection
                          {...route}
                          key={route.title}
                          toggleOpened={toggleOpened}
                          close={close}
                          badge={badge}
                        />
                      );
                    })}
                  </>
                )}
              </Fragment>
            );
          })}
        </Navbar.Section>
      </Navbar>
    );
  }

  if (isMobile) {
    return (
      <Drawer
        opened={opened}
        onClose={close}
        position="left"
        size="xs"
        withCloseButton={false}
        overlayProps={{ blur: 4 }}
      >
        <Group>
          <Logo sx={{ height: "30px" }} white={theme.colorScheme === "dark"} />
          <CloseButton onClick={close} />
        </Group>
        {content()}
      </Drawer>
    );
  }

  if (!isMobile) {
    return <>{content()}</>;
  }
}
